<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <FormulateForm
            v-if="formReady"
            :values="initialFormData"
            :name="formName"
            #default="{ isValid, isLoading }"
            @submit="handleSubmit"
        >
            <FormulateInput
                name="name"
                type="text"
                label="Name"
                placeholder="Name of this item"
                help="A descriptive name, visible to suppliers"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <FormulateInput
                name="description"
                type="textarea"
                label="Description"
                placeholder="Describe what this item is for..."
                rows="3"
                help="Optional - visible to suppliers"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <FormulateInput
                name="layout_type"
                type="select"
                label="Layout Type"
                help="Select how the items should be displayed in this group."
                :options="layoutTypeOptions"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <!-- hide ordering type for now -->
            <!-- <FormulateInput
                name="ordering_type"
                type="select"
                label="Ordering Type"
                help="Select how the items should be ordered in this group."
                :options="orderingTypeOptions"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            /> -->
            <!-- add read-only version_number?  -->
            <div class="my-3 d-flex justify-content-center">
                <b-button
                    variant="gray-secondary"
                    size="lg"
                    class="mr-3"
                    @click="triggerCancel"
                >
                    <span>Cancel</span>
                </b-button>
                <b-button
                    variant="primary"
                    size="lg"
                    :disabled="!isValid || isLoading || saving || isLocked"
                    @click="triggerSubmit"
                >
                    <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                    <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                    <span>{{ saving ? 'Saving' : 'Save' }}</span>
                </b-button>
            </div>
        </FormulateForm>
        <div v-else key="stillLoading" class="min-height-300 w-100 d-flex align-items-center justify-content-center">
            <b-spinner label="Loading..."></b-spinner>
        </div>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'DocHubResourceForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        existingData: {  // if no ID, then we are doing a CREATE, otherwise provide one to do an update
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            saving: false,
            formName: 'dochubGroupForm',
            formReady: false,  // set true when you're done prepping initial data, if any
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            sortOrder: null,  // we grab this out of initial data if it exists, then set it back on submitted data for API if it was not null.... because it is not a field on the form (could make it a hidden one?)
            loadingEnumOptions: false,
            layoutTypeOptions: [],
            orderingTypeOptions: [],
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.existingData?.id;
        },
    },
    async mounted() {
        // do something depending on being a create/update?
        await this.getEnumOptions();
        const existingData = this.existingData || {};
        this.setInitialFormData(existingData);
        this.formReady = true;
        // if (this.isUpdate) {
        //     this.setInitialFormData(existingData);
        //     this.formReady = true;
        // } else {
        //     this.setInitialFormData(existingData);  // still set existingData for create, we likely pass in group (ID)
        //     this.formReady = true;
        // }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setInitialFormData(existingData) {
            // pass in versionData from your API, this has to for
            this.initialFormData = cloneDeep(existingData);
            if (existingData.sort_order || existingData.sort_order === 0) {
                this.sortOrder = existingData.sort_order;
            }
        },
        triggerSubmit() {
            this.$formulate.submit(this.formName);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API - formData is what we get from form.... need to add sortOrder if it was set on initial data
            console.log('data we need to format for save:', formData);
            const dataForApi = cloneDeep(formData);  // copy this first, then we'll add files back in after 
            if (this.sortOrder || this.sortOrder === 0) {
                dataForApi.sort_order = this.sortOrder;
            }
            return dataForApi;
        },
        async getEnumOptions() {
            // get options for a select input
            this.loadingEnumOptions = true;
            let resp;
            try {
                resp = await suppliosApi.dochubGroupManage.enum_options();
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (resp?.data) {
                this.layoutTypeOptions = Array.isArray(resp.data.layout_type) ? resp.data.layout_type : [];  // {value, label}
                this.orderingTypeOptions = Array.isArray(resp.data.ordering_type) ? resp.data.ordering_type : [];  // {value, label}
            }
            this.loadingEnumOptions = false;
        },
        setSavingStatus(isSaving) {  // really only for save loading, not getting initial data
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        async handleSubmit(formData) {  // called on the FormulateForm @submit event
            // this should be async, return a Promise, so Formulate shows isLoading while loading
            console.log('*** From handleSubmit in GROUP form, here is formData we got:');
            console.log(formData);
            let dataToSubmit = this.formatDataForSave(formData);
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    const resourceId = this.existingData.id;
                    const resp = await suppliosApi.dochubGroupManage.update(resourceId, dataToSubmit);  
                    returnData = resp.data;
                    console.log('got response from UPDATING GROUP', returnData);
                    
                } else {  // if NOT an update, create new version
                    const resp = await suppliosApi.dochubGroupManage.create(dataToSubmit);
                    returnData = resp.data;
                    console.log('got response from CREATING GROUP', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common for both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
    }
}
</script>

<style>

</style>